query SupportTickets($filter: TicketFilter!, $first: Int, $after: String) {
  supportTickets(filter: $filter, first: $first, after: $after) {
    totalCount
    edges {
      cursor
      node {
        ...SupportTicketFields
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

query SupportInboxCounts {
  supportInboxCounts {
    unassigned
    mine
    open
    waiting
    untitled
  }
}

query SupportSearch($query: String!, $first: Int) {
  supportSearch(query: $query, first: $first) {
    matchedIn
    messageId
    messageKind
    snippet
    ticket {
      ...SupportTicketFields
    }
  }
}

query SupportTicket($id: ID, $number: String) {
  supportTicket(id: $id, number: $number) {
    ...SupportTicketFields
  }
}

query SupportMessages($ticketId: ID!, $first: Int, $before: String) {
  supportMessages(ticketId: $ticketId, first: $first, before: $before) {
    edges {
      cursor
      node {
        ...SupportMessageFields
      }
    }
    pageInfo {
      hasPreviousPage
      startCursor
    }
  }
}

query SupportTicketEvents($ticketId: ID!, $types: [String!], $first: Int, $after: String) {
  supportTicketEvents(ticketId: $ticketId, types: $types, first: $first, after: $after) {
    edges {
      cursor
      node {
        ...TicketEventFields
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

# Base plan MRR is per currency and excludes variable usage; billingStanding is
# not a health score (Phase 5). The ticket card lists tickets from
# OrganizationSupportTickets, so recentTickets is not requested here.
query SupportClientContext($organizationId: ID!) {
  supportClientContext(organizationId: $organizationId) {
    organizationId
    name
    plans
    mrr {
      amount
      currency
    }
    billingStanding
    overdueInvoices
    openTickets
  }
}

query OrganizationSupportTickets(
  $organizationId: ID!
  $filter: TicketFilter
  $first: Int
  $after: String
) {
  organizationSupportTickets(
    organizationId: $organizationId
    filter: $filter
    first: $first
    after: $after
  ) {
    totalCount
    edges {
      cursor
      node {
        ...SupportTicketFields
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

query SupportConfig {
  ticketTypes(includeRetired: true) {
    id
    key
    name
    icon
    workflowId
    initialStatusKey
    closedStatusKey
    aiEnabled
    order
    retired
  }
  statusWorkflows {
    id
    name
    updatedAt
    statuses {
      key
      name
      category
      order
      retired
    }
    transitions {
      from
      to
    }
  }
}

query SupportSettings {
  supportSettings {
    ticketPrefix
    autoCloseHours
    aiEnabled
    aiDailySpendCapUsd
    clientEmailDelayMinutes
    clientReplyUrl
    widget {
      allowedOrigins
    }
  }
}

query SupportRoles($includeRetired: Boolean) {
  supportRoles(includeRetired: $includeRetired) {
    id
    key
    displayName
    description
    match {
      source
      attributeKey
      values
    }
    articleCount
    isPublic
    aiEnabled
    retired
    contentHints {
      paths
      sections
      keywords
    }
  }
}

query SupportAttributeSummary {
  supportAttributeSummary {
    key
    total
    values {
      value
      count
    }
  }
}

query SupportNotifications($first: Int, $after: String, $unreadOnly: Boolean) {
  supportNotifications(first: $first, after: $after, unreadOnly: $unreadOnly) {
    nodes {
      id
      kind
      ticketId
      ticketNumber
      title
      actorType
      actorId
      readAt
      createdAt
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

query SupportNotificationUnreadCount {
  supportNotificationUnreadCount
}

query SupportNotificationPreferences {
  supportNotificationPreferences {
    kind
    inApp
    email
  }
}

query SupportMetrics($range: SupportDateRangeInput!, $groupBy: SupportMetricGroup!) {
  supportMetrics(range: $range, groupBy: $groupBy) {
    key
    label
    ticketsCreated
    ticketsClosed
    firstResponses
    medianFirstResponseSeconds
    medianResolutionSeconds
  }
}

query SupportSandbox {
  supportSandbox {
    ...SupportSandboxFields
  }
}

query SupportViewerAccess {
  supportViewerAccess {
    canManageSettings
  }
}

# Live teams for the ticket's team picker. Any member may read them.
query SupportTeams {
  teams(includeArchived: false) {
    id
    name
    isArchived
    lead {
      userId
      name
    }
  }
}

query SupportPeople($filter: SupportEndUserFilter, $first: Int, $after: String) {
  supportPeople(filter: $filter, first: $first, after: $after) {
    nodes {
      ...SupportEndUserFields
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

query SupportPerson($id: ID!) {
  supportPerson(id: $id) {
    ...SupportEndUserFields
  }
}

query WebhookEndpoints {
  webhookEndpoints {
    ...WebhookEndpointFields
  }
}

query WebhookDeliveries(
  $endpointId: ID
  $status: WebhookDeliveryStatus
  $first: Int
  $after: String
) {
  webhookDeliveries(endpointId: $endpointId, status: $status, first: $first, after: $after) {
    nodes {
      id
      endpointId
      eventId
      eventType
      ticketId
      ticketNumber
      status
      attempts
      replayCount
      nextAttemptAt
      lastStatusCode
      lastError
      deliveredAt
      createdAt
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}

query SupportInboxFacets {
  supportInboxFacets {
    clients {
      id
      name
    }
    sources
  }
}

query TicketWorkItems($ticketId: ID!) {
  ticketWorkItems(ticketId: $ticketId) {
    connected
    connectionStatus
    live
    defaultListId
    links {
      ...WorkItemLinkFields
    }
    progress {
      currentKey
      currentLabel
      reachedAt
      noticeDueAt
      stages {
        key
        label
        notifyClient
        reached
        current
        announced
      }
    }
  }
}

query ExternalLists {
  externalLists {
    id
    name
    path
  }
}

query ClientStages {
  clientStages {
    version
    isDefault
    notifyAfterClose
    commentOnLinkedTasks
    stages {
      key
      label
      notifyClient
      template
      announceRegressions
      moveToStatusKey
    }
  }
}

query WorkItemStatusMaps {
  workItemStatusMaps {
    listId
    updatedAt
    entries {
      externalStatus
      stageKey
    }
  }
}

query ExternalListStatuses($listId: ID!) {
  externalListStatuses(listId: $listId) {
    name
    color
    type
  }
}

query WorkItemRouting {
  workItemRouting {
    ticketTypeId
    listId
  }
}

query KnowledgeHealth {
  knowledgeHealth {
    failedSources
    failedArticles
    outOfDate
    needsReview
    reembedPending
    attention
  }
}

query ClickUpDocLocations {
  clickUpDocLocations {
    workspaceId
    workspaceName
    scopeType
    id
    name
    path
  }
}

query KnowledgeSources {
  knowledgeSources {
    id
    kind
    name
    rootUrl
    sitemapUrl
    include
    exclude
    audienceRules {
      conditions {
        match
        value
      }
      roles
      everyone
    }
    pageCap
    schedule
    connectionLabel
    trustAudienceLines
    ownershipConfirmed
    status
    lastCrawlAt
    lastError
    consecutiveFailures
    nextAttemptAt
    retrievals
    reguessAudience
    counts {
      status
      count
    }
    lastCrawlStats {
      found
      indexed
      unchanged
      failed
      excluded
      needsReview
      removed
      capped
    }
  }
}

query KnowledgeArticles($filter: KbArticleFilterInput) {
  knowledgeArticles(filter: $filter) {
    hasMore
    total
    nodes {
      id
      sourceId
      url
      linkUrl
      urlPath
      title
      status
      statusReason
      chunkCount
      lastIngestedAt
      updatedAt
      retrievalCount
      audience {
        roles
        plans
        source
        confidence
        evidence
      }
    }
  }
}

query KnowledgeArticlePathGroups($filter: KbArticleFilterInput) {
  knowledgeArticlePathGroups(filter: $filter) {
    prefix
    count
  }
}

query KnowledgeReviewQueue {
  knowledgeReviewQueue {
    id
    sourceId
    url
    urlPath
    title
    status
    statusReason
    chunkCount
    lastIngestedAt
    updatedAt
    retrievalCount
    audience {
      roles
      plans
      source
      confidence
      evidence
    }
  }
}

query AiResolutionUsage($period: String!) {
  aiResolutionUsage(period: $period) {
    period
    confirmed
    assumed
    voided
    billable
    priceUsd
    amountUsd
    tokenCostUsd
    aiSpendUsd
  }
}

query SupportImportRuns {
  supportImportRuns {
    ...SupportImportRunFields
  }
}

query SupportCompareOverview {
  supportCompareOverview {
    importedConversations
    knowledgeArticles
    aiAvailable
    aiUnavailableReason
    latestRun {
      ...SupportCompareRunFields
      metrics {
        ...SupportCompareMetricsFields
      }
      patterns {
        ...SupportComparePatternsFields
      }
    }
  }
}

# One row per past run, with the headline rates, for the history table.
query SupportCompareRuns($first: Int) {
  supportCompareRuns(first: $first) {
    ...SupportCompareRunFields
    metrics {
      ...SupportCompareMetricsFields
    }
  }
}

# One run by id: an older run's full report, opened from the history table.
query SupportCompareRunDetail($id: ID!) {
  supportCompareRun(id: $id) {
    ...SupportCompareRunFields
    metrics {
      ...SupportCompareMetricsFields
    }
    patterns {
      ...SupportComparePatternsFields
    }
  }
}

query SupportCompareEstimate($input: SupportCompareRangeInput!) {
  supportCompareEstimate(input: $input) {
    eligibleConversations
    sampleSize
    perConversationUsd
    estimatedCostUsd
    answerModel
    graderModel
    dailyCapUsd
    spentTodayUsd
    aiAvailable
    aiUnavailableReason
  }
}

query SupportCompareResults(
  $runId: ID!
  $verdict: SupportCompareVerdict
  $gap: SupportCompareGap
  $focus: SupportCompareFocus
  $first: Int
  $after: String
) {
  supportCompareResults(
    runId: $runId
    verdict: $verdict
    gap: $gap
    focus: $focus
    first: $first
    after: $after
  ) {
    totalCount
    endCursor
    hasNextPage
    nodes {
      ...SupportCompareResultFields
    }
  }
}

query KnowledgeAudienceRulePreview($sourceId: ID!, $rules: [KbAudienceRuleInput!]!) {
  knowledgeAudienceRulePreview(sourceId: $sourceId, rules: $rules) {
    total
    unmatched
    rules {
      matches
      keptByAdmin
    }
  }
}

# Guidelines: the support team's instructions to the AI.
query SupportGuidelines {
  supportGuidelines {
    id
    kind
    title
    body
    roles
    live
    pausedReason
    updatedAt
  }
  supportGuidelineLimits {
    maxGuidelineChars
    maxTitleChars
    maxGuidelines
    maxLiveChars
  }
}
